import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth/authSlice";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Tooltip, Skeleton, Empty } from "antd";
import Pagination from "@mui/material/Pagination";
import { unmarkAbsent } from "../services/attendanceService";
import MarkAbsentModal from "./MarkAbsentModal";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined,
  SearchOutlined, ReloadOutlined,
  SafetyCertificateOutlined, StopOutlined, CheckCircleOutlined,
  EyeOutlined, FilterOutlined,
} from "@ant-design/icons";
import { authHeaders } from "../utils/session";

const authH = () => ({ ...authHeaders(), "Content-Type": "application/json" });
const MEMBER_COLORS = ["#5b48e8","#0284c7","#16a34a","#d97706","#dc2626","#7c3aed","#0891b2","#be185d"];
const memberColor = (name = "") => { let h = 0; for (const c of name) h = (h + c.charCodeAt(0)) % MEMBER_COLORS.length; return MEMBER_COLORS[h]; };
const initials = (name = "") => name.trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
const formatDate = (d) => { if (!d) return "—"; return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };
/* The attendance flags arrive as 1/0 on some builds and true/false on others —
   compare on truthiness so neither shape silently hides the controls. */
const isOn = (v) => v === 1 || v === true || v === "1";

/**
 * Attendance cell — the state is what the column is for, so the state is what it
 * shows. The action rides on hover instead of sitting under the pill as a second
 * control, which made every row look like it had something to do.
 */
const AttendanceCell = ({ member, busy, onMarkAbsent, onMarkPresent }) => {
  const [hover, setHover] = useState(false);
  const absent = isOn(member.is_absent_today);
  // Whether this user may mark THIS employee is the server's call, not ours.
  const mayMark = isOn(member.can_mark_attendance);

  const state = absent
    ? { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "Absent" }
    : { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Present" };
  // Hovering offers the opposite of the current state.
  const action = absent
    ? { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Mark Present" }
    : { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Mark Absent" };

  const showAction = mayMark && hover && !busy;
  const shown = busy ? { ...action, label: "Saving…" } : showAction ? action : state;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
      <span
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={mayMark && !busy ? (absent ? onMarkPresent : onMarkAbsent) : undefined}
        title={mayMark ? action.label : undefined}
        style={{
          background: shown.bg, color: shown.color, border: `1px solid ${shown.border}`,
          borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600,
          whiteSpace: "nowrap", cursor: mayMark && !busy ? "pointer" : "default",
          // A fixed box keeps the row from jumping as the label swaps.
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          minWidth: "104px", transition: "all .15s",
        }}
      >
        {shown.label}
      </span>
      {absent && member.absent_until && (
        <span style={{ fontSize: "10.5px", color: "#9ca3af", whiteSpace: "nowrap" }}>
          till {formatDate(member.absent_until)}
        </span>
      )}
    </div>
  );
};

const ManageTeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;
  const debounceRef = useRef(null);
  // Member whose absence is being booked; and the row waiting on unmark-absent.
  const [absentMember, setAbsentMember] = useState(null);
  const [attendanceBusyId, setAttendanceBusyId] = useState(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      /* The API scopes by token now — a manager gets their reporting subtree and
         nobody else's, and `total` counts that same set. So paging, search and
         the role filter all belong to the server; the old client-side subtree
         walk is gone. */
      const res = await axios.get(`${API_BASE_URL}/get-internal-user-list`, {
        headers: authH(),
        params: {
          page,
          limit: PAGE_SIZE,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(roleFilter ? { role: roleFilter } : {}),
        },
      });
      setMembers(res.data?.data || []);
      setTotal(res.data?.total ?? res.data?.pagination?.total_records ?? 0);
    }
    catch { toast.error("Failed to load team members"); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, roleFilter]);

  const fetchRoles = useCallback(async () => {
    try { const res = await axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() }); setRoles(res.data.data || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { fetchMembers(); fetchRoles(); }, [fetchMembers, fetchRoles]);

  useEffect(() => { setPage(1); }, [debouncedSearch, roleFilter]);

  const loggedInUser = (() => { try { return JSON.parse(localStorage.getItem("trade_user_data")); } catch { return null; } })();
  const loggedInCmsRoleId = loggedInUser?.cms_role_id;
  const loggedInRoleObj = roles.find(r => r.id === loggedInCmsRoleId);
  const isSuperAdmin = !loggedInRoleObj
    || (loggedInRoleObj.reports_to?.length === 0 && roles.length > 0)
    || /super/i.test(loggedInRoleObj?.slug || "");

  /* Rows arrive already scoped, searched and filtered — nothing left to do here
     but render them. */
  const rows = members;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const getRoleName = (cmsRoleId) => roles.find(r => r.id === cmsRoleId)?.role_name || "—";

  // Permission flags from menu_access
  const authUser = useSelector(selectUser);
  const menuAccess = authUser?.menu_access || [];
  const teamMemberSubMenu = menuAccess.flatMap(m => m.sub_menus || []).find(s => s.sub_menu_link === "/cms/manage-team-members");
  const canCreate = isSuperAdmin || teamMemberSubMenu?.can_create === 1;
  const canEdit   = isSuperAdmin || teamMemberSubMenu?.can_edit === 1;
  const canDelete = isSuperAdmin || teamMemberSubMenu?.can_delete === 1;

  const handleDelete = async (id, name) => {
    const r = await Swal.fire({ title: `Delete "${name}"?`, text: "This will permanently remove this team member.", icon: "warning", showCancelButton: true, confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280", confirmButtonText: "Yes, delete" });
    if (!r.isConfirmed) return;
    try { await axios.delete(`${API_BASE_URL}/delete-team-member/${id}`, { headers: authH() }); toast.success("Team member deleted"); fetchMembers(); }
    catch (err) {
      // 403 means the target sits outside this user's reporting line.
      const msg = err?.response?.status === 403
        ? err?.response?.data?.message || "You can only manage employees who report to you."
        : err?.response?.data?.message || "Failed to delete";
      toast.error(msg);
    }
  };

  /* Clears today's absence. The backend ends the running range from today, so
     nothing has to be sent — no absence_id to track on the client. */
  const handleMarkPresent = async (member) => {
    const r = await Swal.fire({
      title: `Mark "${member.name}" present?`,
      html: member.absent_until
        ? `Their absence runs till <b>${formatDate(member.absent_until)}</b>. Marking them present ends it and puts them back in the lead round-robin.`
        : "They will be back in the lead round-robin.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, mark present",
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    setAttendanceBusyId(member.id);
    try {
      await unmarkAbsent(member.id);
      toast.success(`${member.name} is back — available for new leads.`);
      fetchMembers();
    } catch (err) {
      toast.error(err?.message || "Could not mark this employee present");
    } finally {
      setAttendanceBusyId(null);
    }
  };

  const handleToggleStatus = async (member) => {
    const isActive = member.status === 1;
    const r = await Swal.fire({ title: isActive ? "Deactivate?" : "Activate?", text: isActive ? `"${member.name}" will lose CMS access.` : `"${member.name}" will regain CMS access.`, icon: isActive ? "warning" : "question", showCancelButton: true, confirmButtonColor: isActive ? "#dc2626" : "#16a34a", cancelButtonColor: "#6b7280", confirmButtonText: isActive ? "Yes, deactivate" : "Yes, activate" });
    if (!r.isConfirmed) return;
    try { await axios.post(`${API_BASE_URL}/update-internal-user-details/${member.id}`, { name: member.name, email: member.email, mobile: member.mobile, role: member.role, cms_role_id: member.cms_role_id, reporting_to_user_id: member.reporting_to_user_id, status: isActive ? 0 : 1, lead_limit: member.lead_limit, invoice_limit: member.invoice_limit }, { headers: authH() }); toast.success(isActive ? "Deactivated" : "Activated"); fetchMembers(); }
    catch { toast.error("Failed to update status"); }
  };

  const thStyle = { padding: "14px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap" };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#0f172a 0%,#334155 50%,#475569 100%)", borderRadius: "16px", padding: "28px 32px", marginBottom: "28px", boxShadow: "0 8px 32px rgba(15,23,42,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,.22)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "white" }}><TeamOutlined /></div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Team Members</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>Manage CMS internal users and their role assignments</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={fetchMembers} style={{ width: "40px", height: "40px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}><ReloadOutlined /></button>
              {canCreate && <Link to="/cms/manage-team-members/create" style={{ display: "flex", alignItems: "center", gap: "7px", height: "40px", padding: "0 20px", borderRadius: "10px", border: "none", background: "white", color: "#0f172a", fontWeight: 700, fontSize: "14px", textDecoration: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}><SafetyCertificateOutlined /> Add Team Member</Link>}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "28px" }}>
            {[
              { label: "Total Members", value: total, icon: <TeamOutlined />, color: "#0f172a", bg: "#f1f5f9" },
              { label: "Active", value: rows.filter(m => m.status === 1).length, icon: <CheckCircleOutlined />, color: "#16a34a", bg: "#f0fdf4" },
              { label: "Inactive", value: rows.filter(m => m.status !== 1).length, icon: <StopOutlined />, color: "#dc2626", bg: "#fef2f2" },
            ].map(s => (
              <div key={s.label} style={{ background: "white", borderRadius: "14px", padding: "20px 24px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ width: "48px", height: "48px", background: s.bg, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: s.color, fontSize: "22px" }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#111827", lineHeight: 1, marginTop: "2px" }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#0f172a,#475569)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "16px" }}><TeamOutlined /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "17px", color: "#1f2937" }}>All Team Members</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>{total} member{total !== 1 ? "s" : ""}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div style={{ position: "relative", width: "240px" }}>
                  <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name/email/mobile..." style={{ width: "100%", height: "38px", paddingLeft: "36px", paddingRight: "16px", border: "1px solid #e5e7eb", borderRadius: "9px", fontSize: "13px", outline: "none", background: "white", color: "#374151", boxSizing: "border-box" }} />
                </div>
                <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ height: "38px", padding: "0 30px 0 12px", border: "1px solid #e5e7eb", borderRadius: "9px", fontSize: "13px", outline: "none", background: "white", color: "#374151", cursor: "pointer", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M2 4l4 4 4-4z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}>
                  <option value="">All Roles</option>
                  {[...new Set(members.map(m => m.role).filter(Boolean))].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: "24px" }}>{[1,2,3,4].map(i => <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: "16px" }} />)}</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>{debouncedSearch || roleFilter ? "No members match" : "No team members yet."}</span>} />
                {!debouncedSearch && !roleFilter && canCreate && <Link to="/cms/manage-team-members/create" style={{ display: "inline-block", marginTop: "16px", padding: "10px 24px", borderRadius: "10px", background: "linear-gradient(135deg,#0f172a,#475569)", color: "white", fontWeight: 600, fontSize: "14px", textDecoration: "none" }}>Add First Member</Link>}
              </div>
            ) : (
              <div className="table-responsive">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg,#f8f9ff 0%,#f1f5f9 100%)", borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ ...thStyle, width: "40px", textAlign: "center" }}>#</th>
                      <th style={thStyle}>Member</th>
                      <th style={thStyle}>Role</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Mobile</th>
                      <th style={thStyle}>Reporting Manager</th>
                      <th style={thStyle}>Services</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Attendance</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                      <th style={thStyle}>Joined</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((member, idx) => {
                      const color = memberColor(member.name);
                      const isActive = member.status === 1;
                      const rowBg = idx % 2 === 0 ? "white" : "#fafbff";
                      const managerName = member.reporting_to_user_name || null;
                      return (
                        <tr key={member.id} style={{ borderBottom: "1px solid #f5f5f5", background: rowBg, transition: "background 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                          <td style={{ padding: "14px 16px", textAlign: "center", fontSize: "13px", color: "#9ca3af", fontWeight: 600 }}>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div style={{ width: "38px", height: "38px", borderRadius: "10px", flexShrink: 0, background: `linear-gradient(135deg, ${color}dd, ${color}88)`, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "13px", fontWeight: 700 }}>{initials(member.name)}</div>
                              <div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>{member.name}</div>
                                <div style={{ fontSize: "11px", color: "#9ca3af" }}>{member.email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            {member.cms_role_name ? <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{member.cms_role_name}</span> : <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#374151" }}>{member.email || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#374151" }}>{member.mobile || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                          <td style={{ padding: "14px 16px" }}>
                            {managerName ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", maxWidth: "180px" }}>
                                <div style={{ width: "30px", height: "30px", borderRadius: "8px", flexShrink: 0, background: "linear-gradient(135deg,#0f172a,#334155)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px" }}>
                                  {managerName.trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                                </div>
                                <div>
                                  <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{managerName}</div>
                                  <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500 }}>Reporting Manager</div>
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: "13px", color: "#d1d5db" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            {(member.services || []).length === 0 ? (
                              <span style={{ fontSize: "13px", color: "#d1d5db" }}>—</span>
                            ) : (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", maxWidth: "220px" }}>
                                {member.services.map(s => (
                                  /* quota: null is unlimited, 0 is paused — a bare
                                     number would read as "no leads" for a pause. */
                                  <Tooltip
                                    key={s.id}
                                    title={`${s.slug} · quota ${s.quota === null || s.quota === undefined ? "unlimited" : Number(s.quota) === 0 ? "paused" : s.quota}`}
                                  >
                                    <span style={{ background: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4", borderRadius: "20px", padding: "2px 9px", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" }}>{s.name}</span>
                                  </Tooltip>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <AttendanceCell
                              member={member}
                              busy={attendanceBusyId === member.id}
                              onMarkAbsent={() => setAbsentMember(member)}
                              onMarkPresent={() => handleMarkPresent(member)}
                            />
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <span style={{ background: isActive ? "#f0fdf4" : "#fef2f2", color: isActive ? "#16a34a" : "#dc2626", border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`, borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600 }}>{isActive ? "Active" : "Inactive"}</span>
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(member.created_at)}</td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                              <Tooltip title="View"><Link to={`/cms/manage-team-members/edit/${member.id}?view=1`} style={{ width: "30px", height: "30px", borderRadius: "7px", border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}><EyeOutlined /></Link></Tooltip>
                              {canEdit && <Tooltip title="Edit"><Link to={`/cms/manage-team-members/edit/${member.id}`} style={{ width: "30px", height: "30px", borderRadius: "7px", border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}><EditOutlined /></Link></Tooltip>}
                              {canEdit && <Tooltip title={isActive ? "Deactivate" : "Activate"}>
                                <button onClick={() => handleToggleStatus(member)} style={{ width: "30px", height: "30px", borderRadius: "7px", border: `1px solid ${isActive ? "#fde68a" : "#bbf7d0"}`, background: isActive ? "#fffbeb" : "#f0fdf4", color: isActive ? "#d97706" : "#16a34a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>{isActive ? <StopOutlined /> : <CheckCircleOutlined />}</button>
                              </Tooltip>}
                              {canDelete && <Tooltip title="Delete"><button onClick={() => handleDelete(member.id, member.name)} style={{ width: "30px", height: "30px", borderRadius: "7px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}><DeleteOutlined /></button></Tooltip>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && total > PAGE_SIZE && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", background: "#fafbff" }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {absentMember && (
        <MarkAbsentModal
          member={absentMember}
          onClose={() => setAbsentMember(null)}
          onDone={fetchMembers}
        />
      )}
    </div>
  );
};

export default ManageTeamMembers;